import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useActiveChannel } from '@/store/activeChannel';

export interface FullPayableRow {
  id: string;
  payee_id: string;
  payee_name: string;
  description: string;
  due_date: string;
  amount_cents: number;
  status: 'pending' | 'overdue' | 'paid' | 'cancelled';
  channel_id: string | null;
}

export interface FullReceivableRow {
  id: string;
  payee_id: string;
  payee_name: string;
  description: string;
  due_date: string;
  amount_cents: number;
  status: 'pending' | 'overdue' | 'received' | 'cancelled';
  channel_id: string | null;
}

export interface PayeeOption {
  id: string;
  name: string;
  kind: 'customer' | 'vendor' | 'both';
}

export interface EntryInput {
  payeeName: string;
  selectedPayeeId: string | null;
  description: string;
  amountCents: number;
  dueDate: string;
  channelId: string | null;
}

const TODAY = () => new Date().toISOString().slice(0, 10);

function translateEntryError(msg: string): string {
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Contato não encontrado. Recarregue a página e tente novamente.';
  if (msg.includes('duplicate') || msg.includes('unique'))
    return 'Este lançamento já foi cadastrado.';
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente. Contate o administrador.';
  if (msg.includes('not-null') || msg.includes('null value'))
    return 'Preencha todos os campos obrigatórios.';
  return 'Erro ao salvar. Verifique sua conexão e tente novamente.';
}

function computeStatus<T extends string>(storedStatus: T, dueDate: string): T {
  if (storedStatus === 'pending' && dueDate < TODAY()) return 'overdue' as T;
  return storedStatus;
}

// ─── Payees ───────────────────────────────────────────────────────────────────

export function usePayees() {
  const { tenantId } = useTenant();
  const [payees, setPayees] = useState<PayeeOption[]>([]);

  const fetch = useCallback(async () => {
    if (!tenantId) return;
    const { data } = await supabase
      .from('payees')
      .select('id, name, kind')
      .eq('tenant_id', tenantId)
      .order('name');
    setPayees((data as unknown as PayeeOption[]) ?? []);
  }, [tenantId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { payees, refetch: fetch };
}

async function ensurePayee(
  tenantId: string,
  name: string,
  selectedId: string | null,
  kind: 'vendor' | 'customer'
): Promise<string> {
  if (selectedId) return selectedId;

  const { data: existing } = await supabase
    .from('payees')
    .select('id')
    .eq('tenant_id', tenantId)
    .ilike('name', name.trim())
    .limit(1)
    .maybeSingle();

  if (existing) return (existing as unknown as { id: string }).id;

  const { data: created, error } = await supabase
    .from('payees')
    .insert({ tenant_id: tenantId, name: name.trim(), kind } as never)
    .select('id')
    .single();
  if (error) throw new Error('Erro ao criar contato: ' + error.message);
  return (created as unknown as { id: string }).id;
}

// ─── A Pagar ──────────────────────────────────────────────────────────────────

export function usePayables() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<FullPayableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    let q = supabase
      .from('accounts_payable')
      .select('id, payee_id, description, amount_cents, due_date, status, channel_id, payee:payees(name)')
      .eq('tenant_id', tenantId)
      .order('due_date');
    if (activeChannelId) q = q.eq('channel_id', activeChannelId);
    const { data: rows, error } = await q;
    if (error) { setFetchError('Erro ao carregar contas a pagar.'); setLoading(false); return; }

    type RawAP = {
      id: string; payee_id: string; description: string; amount_cents: number;
      due_date: string; status: string; channel_id: string | null;
      payee: { name: string } | null;
    };
    const ap = (rows ?? []) as unknown as RawAP[];
    setData(ap.map((r) => ({
      id: r.id,
      payee_id: r.payee_id,
      payee_name: r.payee?.name ?? '—',
      description: r.description,
      due_date: r.due_date,
      amount_cents: r.amount_cents,
      status: computeStatus(r.status as FullPayableRow['status'], r.due_date),
      channel_id: r.channel_id,
    })));
    setLoading(false);
  }, [tenantId, activeChannelId]);

  useEffect(() => { fetch(); }, [fetch]);

  async function createPayable(input: EntryInput): Promise<void> {
    if (!tenantId) throw new Error('Tenant não encontrado');
    const payeeId = await ensurePayee(tenantId, input.payeeName, input.selectedPayeeId, 'vendor');
    const { error } = await supabase.from('accounts_payable').insert({
      tenant_id: tenantId,
      payee_id: payeeId,
      description: input.description,
      amount_cents: input.amountCents,
      due_date: input.dueDate,
      channel_id: input.channelId,
      status: 'pending',
    } as never);
    if (error) throw new Error(translateEntryError(error.message));
    await fetch();
  }

  async function markPaid(id: string): Promise<void> {
    const { error } = await supabase
      .from('accounts_payable')
      .update({ status: 'paid', paid_at: new Date().toISOString() } as never)
      .eq('id', id);
    if (error) throw new Error('Erro ao marcar como paga. Tente novamente.');
    await fetch();
  }

  async function cancelPayable(id: string): Promise<void> {
    const { error } = await supabase
      .from('accounts_payable')
      .update({ status: 'cancelled' } as never)
      .eq('id', id);
    if (error) throw new Error('Erro ao cancelar lançamento. Tente novamente.');
    await fetch();
  }

  return { data, loading, fetchError, refetch: fetch, createPayable, markPaid, cancelPayable };
}

// ─── A Receber ────────────────────────────────────────────────────────────────

export function useReceivables() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<FullReceivableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    let q = supabase
      .from('accounts_receivable')
      .select('id, payee_id, description, amount_cents, due_date, status, channel_id, payee:payees(name)')
      .eq('tenant_id', tenantId)
      .order('due_date');
    if (activeChannelId) q = q.eq('channel_id', activeChannelId);
    const { data: rows, error } = await q;
    if (error) { setFetchError('Erro ao carregar contas a receber.'); setLoading(false); return; }

    type RawAR = {
      id: string; payee_id: string; description: string; amount_cents: number;
      due_date: string; status: string; channel_id: string | null;
      payee: { name: string } | null;
    };
    const ar = (rows ?? []) as unknown as RawAR[];
    setData(ar.map((r) => ({
      id: r.id,
      payee_id: r.payee_id,
      payee_name: r.payee?.name ?? '—',
      description: r.description,
      due_date: r.due_date,
      amount_cents: r.amount_cents,
      status: computeStatus(r.status as FullReceivableRow['status'], r.due_date),
      channel_id: r.channel_id,
    })));
    setLoading(false);
  }, [tenantId, activeChannelId]);

  useEffect(() => { fetch(); }, [fetch]);

  async function createReceivable(input: EntryInput): Promise<void> {
    if (!tenantId) throw new Error('Tenant não encontrado');
    const payeeId = await ensurePayee(tenantId, input.payeeName, input.selectedPayeeId, 'customer');
    const { error } = await supabase.from('accounts_receivable').insert({
      tenant_id: tenantId,
      payee_id: payeeId,
      description: input.description,
      amount_cents: input.amountCents,
      due_date: input.dueDate,
      channel_id: input.channelId,
      status: 'pending',
    } as never);
    if (error) throw new Error(translateEntryError(error.message));
    await fetch();
  }

  async function markReceived(id: string): Promise<void> {
    const { error } = await supabase
      .from('accounts_receivable')
      .update({ status: 'received', received_at: new Date().toISOString() } as never)
      .eq('id', id);
    if (error) throw new Error('Erro ao marcar como recebida. Tente novamente.');
    await fetch();
  }

  async function cancelReceivable(id: string): Promise<void> {
    const { error } = await supabase
      .from('accounts_receivable')
      .update({ status: 'cancelled' } as never)
      .eq('id', id);
    if (error) throw new Error('Erro ao cancelar lançamento. Tente novamente.');
    await fetch();
  }

  return { data, loading, fetchError, refetch: fetch, createReceivable, markReceived, cancelReceivable };
}
